import React, { useEffect, useMemo, useState } from "react";

import moment from "moment";

import {
  TFunction,
  checkFeatureFlag,
  formatCurrencyRange,
  formatNumericCurrency,
  getEnvironment,
  isSingleFieldOutcomes,
  someOutcomeHasErrorOfType,
  useGetFarmPayments,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldEvaluationMetricCorrections,
  useInventoryImpact,
  useTranslation,
} from "utils";

import { CURRENCY_CODE } from "consts";

import {
  CARBON_RUNNER_ERROR_TYPE,
  CarbonProgramOutcomesResponse,
  EVALUATION_METRICS,
  IMPACT_PANEL_CALCULATION_TYPE,
  SingleFieldOutcomes,
  VIEW_TYPE,
} from "models";

import { Col, Flex, Icon, IconLinkButton, LoadingOverlay, Option, PayoutImpactCard, Select, Tooltip, Typography } from "components";

import { ImpactPanelCollapseToggle } from "pages/app/all-field-view/components/CollapsibleImpactPanel";
import { SubmissionSuccessModalContent } from "pages/app/all-field-view/components/SubmissionSuccessModal";
import { EvaluationMetricsPanel } from "pages/app/field-timeline/components/EvaluationMetricsPanel";
import { ImpactPanelErrorView } from "pages/app/field-timeline/components/ImpactPanelErrorView";
import { MissingDataGuidance } from "pages/app/field-timeline/components/MissingDataGuidance";
import { OutcomeBasedImpact } from "pages/app/field-timeline/components/OutcomeBasedImpact";
import { PracticeBasedImpact } from "pages/app/field-timeline/components/PracticeBasedImpact";
import { calculateImpactPanelBodyHeight } from "pages/app/field-timeline/utils/TimelineUtils";

type InventoryImpactPanelProps = {
  carbonProgramOutcomes?: CarbonProgramOutcomesResponse;
  fieldSizes: Record<string, number>;
  progress: number;
  shouldShowSubmissionSuccessModalContent?: boolean;
  hidePayouts?: boolean;
};

const FullPaymentBadge = ({ t }: { t: TFunction }) => (
  <Flex className="maximum-payment-badge" data-testid="full-payment-badge" align="center" gap="0.5rem">
    <Icon iconName="klim/medal" color="white" />
    <Typography variant="paragraph-s" bold color="white">
      {t("impactPanel.inventory.fullPaymentBadge")}
    </Typography>
  </Flex>
);

const EmptyDataView = ({ t }: { t: TFunction }) => (
  <div data-testid="field-timeline-result-card-empty" className="field-timeline-result-card">
    <div className="field-timeline-result-card container empty">
      <Flex vertical={true} align="center" justify="center" style={{ height: "100%" }}>
        <Icon dataTestId="field-timeline-result-card-empty-payout-funding-icon" className="margin-bottom-100" iconName="klim/funding" size="xl" />
        <Typography variant="paragraph-m" data-testid="field-timeline-result-card-empty-title" className="field-timeline-result-card-empty-title">
          {t("impactPanel.emptyPayoutTitle")}
        </Typography>
        <Typography
          variant="paragraph-m"
          data-testid="field-timeline-result-card-empty-payout-description"
          className="field-timeline-result-card-empty-payout-description"
        >
          {t("impactPanel.emptyPayoutDescription")}
        </Typography>
      </Flex>
    </div>
  </div>
);

export const InventoryImpactPanel = ({
  carbonProgramOutcomes,
  fieldSizes,
  progress,
  shouldShowSubmissionSuccessModalContent = false,
  hidePayouts = false,
}: InventoryImpactPanelProps) => {
  const { t } = useTranslation(["fieldTimeline"]);
  const env = getEnvironment();
  const areEvaluationMetricsEnabled = env.production ? checkFeatureFlag("evaluation-metrics") : true;
  const shouldHidePanel = !areEvaluationMetricsEnabled && hidePayouts;

  const [selectedToggleOption, setSelectedToggleOption] = useState<IMPACT_PANEL_CALCULATION_TYPE>(IMPACT_PANEL_CALCULATION_TYPE.TOTAL);
  const [selectedPanel, setSelectedPanel] = useState<"payout" | EVALUATION_METRICS>(hidePayouts ? EVALUATION_METRICS.HUMUS : "payout");
  const singleFieldOutcomes: SingleFieldOutcomes[] = Object.values(carbonProgramOutcomes ?? {}).filter(isSingleFieldOutcomes);

  const panelMetrics = areEvaluationMetricsEnabled ? ["payout", ...Object.values(EVALUATION_METRICS)] : ["payout"];

  const { currentFarm } = useGetFarms();
  const carbonProgram = currentFarm?.carbonProgram;
  const {
    data: { activeFarmReportingYear = moment().year() },
  } = useGetFarmReportingYear(currentFarm?.id || null);

  const { totalPayoutMin, totalPayoutMax, isMaxTotalPayoutReached, hideInventoryPayouts, maximumPotentialPayout, displayPerHectareOrTotalFarm } =
    useInventoryImpact(activeFarmReportingYear, singleFieldOutcomes, currentFarm?.id ?? "", carbonProgram ?? undefined);

  const [selectedYear, setSelectedYear] = useState<number>(activeFarmReportingYear);

  useEffect(() => {
    setSelectedYear(activeFarmReportingYear);
  }, [activeFarmReportingYear]);

  const { data: farmPayments } = useGetFarmPayments(!hidePayouts);

  const pastYears = useMemo(() => {
    const years = farmPayments?.payments?.filter((p) => p.year < activeFarmReportingYear).map((p) => p.year) ?? [];
    return [...new Set(years)].sort((a, b) => b - a);
  }, [farmPayments, activeFarmReportingYear]);

  const isActiveYearSelected = selectedYear === activeFarmReportingYear;
  const selectedPastPayment = !isActiveYearSelected ? farmPayments?.payments?.find((p) => p.year === selectedYear) : undefined;

  // Calculate per hectare values when toggle is set to PER_HA
  const isPerHectare = selectedToggleOption === IMPACT_PANEL_CALCULATION_TYPE.PER_HA;

  const displayedTotalPayoutMin = displayPerHectareOrTotalFarm(totalPayoutMin, isPerHectare);
  const displayedTotalPayoutMax = displayPerHectareOrTotalFarm(totalPayoutMax, isPerHectare);
  const displayedMaximumPotentialPayout = displayPerHectareOrTotalFarm(maximumPotentialPayout ?? 0, isPerHectare);

  const toggleOptions = [
    { label: t("impactPanel.total"), value: IMPACT_PANEL_CALCULATION_TYPE.TOTAL },
    { label: t("impactPanel.perHa"), value: IMPACT_PANEL_CALCULATION_TYPE.PER_HA },
  ];

  const fieldIds = Object.keys(carbonProgramOutcomes ?? {});
  const { data: existingCorrectionsByFieldId } = useGetFieldEvaluationMetricCorrections({ fieldIds: areEvaluationMetricsEnabled ? fieldIds : [] });

  const bodyHeight = calculateImpactPanelBodyHeight();

  const hasErrorData = useMemo(
    () => someOutcomeHasErrorOfType(carbonProgramOutcomes ?? {}, CARBON_RUNNER_ERROR_TYPE.FOOTPRINT_SERVICE),
    [carbonProgramOutcomes]
  );

  return !shouldHidePanel ? (
    <Flex className="field-timeline-impact-panel container inventory-impact-panel" data-testid="field-timeline-impact-panel" vertical={true}>
      <div className="field-timeline-impact-panel body" style={{ height: `${bodyHeight}rem` }}>
        <Flex vertical>
          <Flex align="center" justify="space-between" className="margin-bottom-100">
            <Flex align="center">
              <Select
                className="panel-select"
                dataTestId="panel-select"
                name="panelSelect"
                popupMatchSelectWidth={false}
                onChange={setSelectedPanel}
                defaultValue={selectedPanel}
                value={selectedPanel}
              >
                {panelMetrics.map((metric, i) => (
                  <Option
                    data-testid={`panel-select-option-${i}`}
                    value={metric}
                    key={i}
                    className="panel-select-option"
                    disabled={metric === "payout" && hidePayouts}
                  >
                    {metric === "payout" ? t("impactPanel.inventory.totalPayout") : t(`impactPanel.evaluationMetrics.${metric}`)}
                  </Option>
                ))}
              </Select>
              {selectedPanel === "payout" && (
                <Tooltip placement="top" title={t("impactPanel.inventory.totalPayoutTitle", { programName: carbonProgram?.programName })}>
                  <IconLinkButton
                    className="payout-info-icon-button"
                    dataTestId="inventory-impact-panel-total-payout-info-icon-button"
                    theme="grey"
                    iconType="info"
                    size="small"
                  />
                </Tooltip>
              )}
            </Flex>
            <ImpactPanelCollapseToggle />
          </Flex>
          {selectedPanel === "payout" && !hasErrorData && !hideInventoryPayouts && (
            <>
              <Flex justify="space-between" align="center">
                <Select
                  key={selectedYear}
                  className="payout-years-select"
                  dataTestId="payout-year-select"
                  name="payoutYearSelect"
                  popupMatchSelectWidth={false}
                  popupClassName="payout-years-select-popup"
                  defaultValue={selectedYear}
                  value={selectedYear}
                  onChange={(value) => {
                    setSelectedYear(value);
                    if (value < activeFarmReportingYear) setSelectedToggleOption(IMPACT_PANEL_CALCULATION_TYPE.TOTAL);
                  }}
                >
                  <Option value={activeFarmReportingYear} key={activeFarmReportingYear} className="payout-years-select-option">
                    {activeFarmReportingYear}
                  </Option>
                  {pastYears.map((year, i) => (
                    <Option data-testid={`payout-year-select-option-${i}`} value={year} key={year} className="payout-years-select-option">
                      {year}
                    </Option>
                  ))}
                </Select>
                <Select
                  key={selectedToggleOption}
                  className="field-timeline-result-select"
                  dataTestId="field-timeline-result-select"
                  name="resultSelect"
                  popupMatchSelectWidth={false}
                  onChange={setSelectedToggleOption}
                  defaultValue={selectedToggleOption}
                  value={selectedToggleOption}
                >
                  {toggleOptions.map((option, i) => (
                    <Option
                      data-testid={`field-timeline-result-select-option-${i}`}
                      value={option.value}
                      key={i}
                      className="field-timeline-result-select-option"
                      disabled={option.value === IMPACT_PANEL_CALCULATION_TYPE.PER_HA && !isActiveYearSelected}
                    >
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </Flex>
              <span className="dotted-line-divider" />
            </>
          )}
        </Flex>
        {progress === 100 ? (
          <>
            {selectedPanel !== "payout" ? (
              <EvaluationMetricsPanel
                selectedMetric={selectedPanel}
                carbonProgramOutcomes={carbonProgramOutcomes ?? {}}
                reportingYear={activeFarmReportingYear}
                fieldSizes={fieldSizes}
                // right now, the inventory panel is only available for the AFV page
                viewType={VIEW_TYPE.ALL_FIELDS}
                existingCorrectionsByFieldId={existingCorrectionsByFieldId ?? {}}
                reportingPeriod={carbonProgram?.fieldsManagement.reportingPeriod}
              />
            ) : hasErrorData ? (
              <>
                <ImpactPanelErrorView />
              </>
            ) : hideInventoryPayouts ? (
              <>
                <EmptyDataView t={t} />
              </>
            ) : shouldShowSubmissionSuccessModalContent ? (
              <>
                <SubmissionSuccessModalContent carbonProgramOutcomes={carbonProgramOutcomes ?? null} farmId={currentFarm?.id ?? ""} variant="panel" />
              </>
            ) : !isActiveYearSelected ? (
              <>
                {selectedPastPayment && (
                  <PayoutImpactCard
                    payout={selectedPastPayment.amount}
                    deductedPayout={selectedPastPayment.deductedAmount}
                    currency={carbonProgram?.currency}
                    invoiceDate={selectedPastPayment.date ?? undefined}
                    icons={[{ icon: "tabler/wallet" }]}
                    variant="invoice"
                    year={selectedPastPayment.year}
                    unsigned={true}
                    dataTestId="payout-year-select-past-payment-card"
                  />
                )}
              </>
            ) : (
              <>
                {carbonProgram?.platformFeatures.fieldManagement?.outcomeBasedIncentives && (
                  <>
                    <Flex className="margin-top-50" justify="space-between">
                      {isMaxTotalPayoutReached ? (
                        <FullPaymentBadge t={t} />
                      ) : (
                        <Tooltip placement="top" title={t("impactPanel.inventory.totalPayouts")}>
                          <Typography variant="h4" data-testid="total-payout" color="green-400">
                            {formatCurrencyRange(displayedTotalPayoutMin, displayedTotalPayoutMax, {
                              decimals: 0,
                              currency: carbonProgram.currency,
                            })}
                          </Typography>
                        </Tooltip>
                      )}
                      {carbonProgram.fieldsManagement.maximumPayoutPerHa !== null && (
                        <Tooltip placement="top" title={t("impactPanel.inventory.totalPayoutPotential")}>
                          <Typography
                            variant="h4"
                            color={isMaxTotalPayoutReached ? "green-400" : undefined}
                            className={isMaxTotalPayoutReached ? undefined : "maximum-theoretical-payout"}
                            data-testid="maximum-potential-payout"
                          >
                            {formatNumericCurrency(displayedMaximumPotentialPayout, { decimals: 0, currency: carbonProgram.currency })}
                          </Typography>
                        </Tooltip>
                      )}
                    </Flex>
                    <OutcomeBasedImpact
                      isDividePerHectare={isPerHectare}
                      reportingYear={activeFarmReportingYear}
                      maximumPayoutPerHa={carbonProgram.fieldsManagement.maximumPayoutPerHa}
                      singleFieldOutcomes={singleFieldOutcomes}
                    />
                  </>
                )}
                {carbonProgram?.platformFeatures.fieldManagement?.practiceBasedIncentives && (
                  <PracticeBasedImpact
                    singleFieldOutcomes={singleFieldOutcomes}
                    reportingYear={activeFarmReportingYear}
                    isDividePerHectare={isPerHectare}
                    currency={carbonProgram?.currency ?? CURRENCY_CODE.EUR}
                  />
                )}
                <MissingDataGuidance />
              </>
            )}
          </>
        ) : (
          <Flex align="center" justify="center" vertical>
            <LoadingOverlay>
              <Col className="loading-message">
                <Typography variant="paragraph-s">{t("impactPanel.fieldsCalculated", { progress })}</Typography>
              </Col>
            </LoadingOverlay>
          </Flex>
        )}
      </div>
    </Flex>
  ) : null;
};
